import React, {useEffect, useState, useCallback} from "react";
import {Card} from "react-bootstrap";
import {Photo, User} from "../../types/Types";
import {collection, getDocs, query, where, Timestamp, doc, getDoc} from "@firebase/firestore";
import {db} from "../../../firebase/FirebaseProvider";
import PhotoRow from "../../locations/profile/PhotoRow";
import {logDataLoaded} from "../../util/Util";

interface Props {
    socialDisabled: boolean;
    currentUser: User;
}

function PhotoOfTheMonthWidget(props: Props) {
    const [photo, setPhoto] = useState<Photo | undefined>(undefined);
    const [photoUser, setPhotoUser] = useState<User | undefined>(undefined);
    const [likesMap, setLikesMap] = useState<Map<string, boolean>>(new Map());
    const [loading, setLoading] = useState<boolean>(true);

    const loadPhotoOfTheMonth = useCallback(async() => {
        setLoading(true);

        // Calculate previous month
        const now = new Date();
        const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfPreviousMonth = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1);
        const endOfPreviousMonth = new Date(currentMonth.getTime() - 1); // Last millisecond of previous month

        // Convert to Firestore Timestamps for query
        const startTimestamp = Timestamp.fromDate(startOfPreviousMonth);
        const endTimestamp = Timestamp.fromDate(endOfPreviousMonth);

        // Fetch likes from previous month
        const likesQuery = query(collection(db, 'likes'),
            where('likeType', '==', 'PHOTO'),
            where('createdAt', '>', startTimestamp),
            where('createdAt', '<=', endTimestamp));
        const likesSnapshot = await getDocs(likesQuery);
        
        logDataLoaded('photo of month likes', likesSnapshot.size);
        
        // Count likes per photo
        const likeCounts = new Map<string, number>();
        likesSnapshot.forEach((doc) => {
            const likeData = doc.data();
            const entityId = likeData.entityId;
            likeCounts.set(entityId, (likeCounts.get(entityId) || 0) + 1);
        });

        if (likeCounts.size === 0) {
            setPhoto(undefined);
            setLoading(false);
            return;
        }

        // Find photo with most likes, if tie, the newer one
        let maxLikes = 0;
        const candidates: string[] = [];

        likeCounts.forEach((count, photoId) => {
            if (count > maxLikes) {
                maxLikes = count;
                candidates.length = 0;
                candidates.push(photoId);
            } else if (count === maxLikes) {
                candidates.push(photoId);
            }
        });

        if (candidates.length === 0) {
            setPhoto(undefined);
            setLoading(false);
            return;
        }

        // Fetch all candidate photos
        const photoPromises = candidates.map(async (photoId, index) => {
            const doc = await getDocs(query(collection(db, 'photos'), where('id', '==', photoId)));
            const photoData = doc.docs[0]?.data();
            if (photoData) {
                const photo = photoData as Photo;
                photo.id = photoId;
                // Convert Firestore Timestamp to Date
                if (photo.createdAt && typeof photo.createdAt !== 'object') {
                    photo.createdAt = (photo.createdAt as any).toDate();
                }
                return photo;
            }
            return null;
        });
        const photos = (await Promise.all(photoPromises)).filter(p => p) as Photo[];

        // Sort by createdAt desc (newer first)
        photos.sort((a, b) => {
            const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
            const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
            return bTime - aTime;
        });

        const selectedPhoto = photos[0];
        if (selectedPhoto) {
            setPhoto(selectedPhoto);
            
            // Fetch the user who took the photo
            const userDoc = await getDoc(doc(db, 'users', selectedPhoto.userId));
            if (userDoc.exists()) {
                setPhotoUser(userDoc.data() as User);
            }
            
            // Check if current user has liked this photo
            const likesQuery = query(
                collection(db, 'likes'),
                where('userId', '==', props.currentUser.id),
                where('entityId', '==', selectedPhoto.id),
                where('likeType', '==', 'PHOTO')
            );
            const likesSnapshot = await getDocs(likesQuery);
            const newLikesMap = new Map<string, boolean>();
            newLikesMap.set(selectedPhoto.id, !likesSnapshot.empty);
            setLikesMap(newLikesMap);
        }
        setLoading(false);
    }, [props.currentUser.id]);

    useEffect(() => {
        loadPhotoOfTheMonth();
    }, [loadPhotoOfTheMonth]);

    if (props.socialDisabled) {
        return null;
    }

    // Calculate previous month for display
    const now = new Date();
    const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const monthName = previousMonthDate.toLocaleString('default', { month: 'long' });
    const year = previousMonthDate.getFullYear();

    return (
        <Card className={"mt-3 mb-3"}>
            <Card.Header>
                <div><b>Photo of the Month - {monthName} {year}</b></div>
            </Card.Header>
            <Card.Body style={{padding: "0em"}}>
                {loading ? (
                    <div className={'loading'}>Loading...</div>
                ) : photo && photoUser ? (
                    <div className={"album"}>
                        <PhotoRow
                            photo={photo}
                            photoUser={photoUser}
                            currentUser={props.currentUser}
                            displayLocation={true}
                            displayEditButton={false}
                            likesMap={likesMap}
                            disableImageNavigation={false}
                            hideLikeButton={true}
                            hideTimestamp={false}
                        />
                    </div>
                ) : (
                    <div className={'loading'}>No photo of the month yet</div>
                )}
            </Card.Body>
        </Card>
    );
}

export default PhotoOfTheMonthWidget;